import { ActivityType } from "../../../../types";
import {
  PlanningSummaryGenerationFailedError,
  PlanningSummaryNoActivitiesError,
  type PlanningSummarySections,
} from "../../../../services/planning-summaries";

export interface PlanningSummaryErrorNotification {
  title: string;
  description: string;
}

// Maps the three known failure shapes to distinct, neutral-Spanish copy so the
// editor knows whether retrying is worth it and whether their existing text
// survived the failed attempt.
export function resolvePlanningSummaryErrorNotification(
  error: unknown,
): PlanningSummaryErrorNotification {
  if (error instanceof PlanningSummaryNoActivitiesError) {
    return {
      title: "Sin actividades",
      description:
        "Esta sección no tiene actividades vinculadas; no es posible generar un resumen.",
    };
  }
  if (error instanceof PlanningSummaryGenerationFailedError) {
    return {
      title: "Error al generar",
      description:
        "Ocurrió un error al generar el resumen. El resumen anterior se conservó sin cambios; puedes intentarlo de nuevo.",
    };
  }
  return {
    title: "Error",
    description: "Ocurrió un error al generar el resumen de la sección.",
  };
}

// Both the SCRUM-717 summary and the super summary are diffed on `text` alone.
type SectionTexts = Record<ActivityType, { text: string | null }>;

// Manual edits are PATCHed on form submit rather than per keystroke, so
// handleSubmit needs to know which sections actually changed since load.
export function getChangedPlanningSummarySections(
  current: SectionTexts,
  original: SectionTexts,
): ActivityType[] {
  return (Object.keys(current) as ActivityType[]).filter(
    (type) => current[type].text !== original[type].text,
  );
}

const PLANNING_SUMMARY_FIELD_BY_TYPE: Record<ActivityType, string> = {
  [ActivityType.START]: "planning_summary_start",
  [ActivityType.MIDDLE]: "planning_summary_middle",
  [ActivityType.CLOSING]: "planning_summary_closing",
};

const PLANNING_SUPER_SUMMARY_FIELD_BY_TYPE: Record<ActivityType, string> = {
  [ActivityType.START]: "planning_super_summary_start",
  [ActivityType.MIDDLE]: "planning_super_summary_middle",
  [ActivityType.CLOSING]: "planning_super_summary_closing",
};

function buildChangedTextFields(
  fieldByType: Record<ActivityType, string>,
  current: SectionTexts,
  original: SectionTexts,
): Record<string, string> {
  return getChangedPlanningSummarySections(current, original).reduce<
    Record<string, string>
  >((fields, type) => {
    fields[fieldByType[type]] = current[type].text ?? "";
    return fields;
  }, {});
}

// Summaries now ride the lesson PATCH instead of their own request, and they
// carry no timestamp of their own — so this only ever sends changed text.
export function buildPlanningSummaryLessonFields(
  current: PlanningSummarySections,
  original: PlanningSummarySections,
): Record<string, string> {
  return buildChangedTextFields(
    PLANNING_SUMMARY_FIELD_BY_TYPE,
    current,
    original,
  );
}

// An empty string is sent as-is: the server reads it as "back to automatic".
export function buildPlanningSuperSummaryLessonFields(
  current: SectionTexts,
  original: SectionTexts,
): Record<string, string> {
  return buildChangedTextFields(
    PLANNING_SUPER_SUMMARY_FIELD_BY_TYPE,
    current,
    original,
  );
}
